import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ApiError, apiJson, setAccessToken } from '@/lib/apiClient';

/**
 * Staff authentication for the admin portal (slice 17).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 A TENANT USER MUST NOT BE ABLE TO SIGN IN HERE, AND THE SERVER SAYS SO.
 * ─────────────────────────────────────────────────────────────────────────────
 * `/auth/login` authenticates BOTH principals — it is the same endpoint the
 * customer dashboard uses, and a tenant owner's credentials are valid against
 * it. What distinguishes them is `principal` on `/api/me`.
 *
 * THE DOOR IS THE SERVER'S AUDIENCE CHECK, NOT THIS FILE. `login()` sends
 * `audience: 'staff'` (SEAM B, 17i); the server refuses a non-staff principal at
 * that door generically, so on a fresh bundle a customer never reaches the
 * `not_staff` branch below at all. What remains here is the SECOND layer plus
 * the clean-up for the stale-bundle path: if a server that has not yet learned
 * the audience field still ACCEPTS the login and sets `raad_rt` on the `admin.`
 * origin, this asks the server what the token is (`/api/me`, never a client-side
 * JWT decode — that could be lied to), and on a non-staff answer it BOTH discards
 * the in-memory token AND calls `/auth/logout` so the leaked refresh cookie does
 * not survive to mint a fresh session on the next boot. A hidden screen with a
 * live cookie is not a closed door.
 */

export interface StaffUser {
  id: string;
  name: string | null;
  phone: string;
  role: 'super_admin' | 'admin_staff';
}

type State =
  | { status: 'loading' }
  | { status: 'authenticated'; user: StaffUser }
  | { status: 'anonymous' };

interface AuthContextValue {
  state: State;
  login: (phone: string, password: string) => Promise<{ ok: true } | { ok: false; reason: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface MeResponse {
  id: string;
  name: string | null;
  phone: string;
  role: string;
  principal?: string;
  tenantId?: string | null;
}

/**
 * The server's own answer to "what is this token", narrowed to staff. This is
 * defence-in-depth beneath the server-side audience door (17i), not the guard
 * itself — the door refuses a non-staff principal before this ever sees a
 * non-staff body on a current server. It still runs so the stale-bundle path is
 * covered.
 */
function asStaff(me: MeResponse): StaffUser | null {
  if (me.principal !== 'staff') return null;
  if (me.role !== 'super_admin' && me.role !== 'admin_staff') return null;
  return { id: me.id, name: me.name, phone: me.phone, role: me.role };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });

  /** Recover a session from the httpOnly refresh cookie — this is what makes
   * "access token in memory only" survivable across a reload (slice 17a). */
  const restore = useCallback(async () => {
    try {
      const tokens = await apiJson<{ accessToken: string }>('/auth/refresh', { method: 'POST' });
      setAccessToken(tokens.accessToken);
      const me = await apiJson<MeResponse>('/api/me');
      const staff = asStaff(me);
      if (staff === null) {
        setAccessToken(null);
        setState({ status: 'anonymous' });
        return;
      }
      setState({ status: 'authenticated', user: staff });
    } catch {
      setAccessToken(null);
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void restore();
  }, [restore]);

  const login: AuthContextValue['login'] = useCallback(async (phone, password) => {
    try {
      const tokens = await apiJson<{ accessToken: string }>('/auth/login', {
        method: 'POST',
        // SEAM B (17i): name the door. The server refuses a non-staff principal
        // at `audience: 'staff'` generically; it treats the field as OPTIONAL and
        // falls back to today's behaviour when absent, so a stale bundle is safe.
        body: JSON.stringify({ phone, password, audience: 'staff' }),
      });
      setAccessToken(tokens.accessToken);

      const me = await apiJson<MeResponse>('/api/me');
      const staff = asStaff(me);
      if (staff === null) {
        // Reached only on a stale-bundle / pre-audience server that ACCEPTED a
        // non-staff login and set `raad_rt` on this origin. Discard the in-memory
        // token AND `/auth/logout` to revoke the leaked refresh cookie — without
        // the logout, the next boot `restore()` mints a fresh session from it.
        // Best-effort on the wire; the local session ends regardless.
        setAccessToken(null);
        try {
          await apiJson('/auth/logout', { method: 'POST' });
        } catch {
          /* the cookie stays only if the server was unreachable; local state is clear */
        }
        setState({ status: 'anonymous' });
        return { ok: false, reason: 'not_staff' };
      }
      setState({ status: 'authenticated', user: staff });
      return { ok: true };
    } catch (e) {
      setAccessToken(null);
      setState({ status: 'anonymous' });
      // 401 is a wrong credential; anything else is the service being unwell,
      // and telling those apart is the difference between "check your password"
      // and "this is not your fault".
      const reason = e instanceof ApiError && e.status === 401 ? 'bad_credentials' : 'unavailable';
      return { ok: false, reason };
    }
  }, []);

  const logout: AuthContextValue['logout'] = useCallback(async () => {
    try {
      await apiJson('/auth/logout', { method: 'POST' });
    } catch {
      // Logout is best-effort on the wire; the local session ends regardless.
      // Leaving a token in memory because the server was unreachable would be
      // the wrong failure direction.
    }
    setAccessToken(null);
    setState({ status: 'anonymous' });
  }, []);

  const value = useMemo(() => ({ state, login, logout }), [state, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useStaffAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useStaffAuth used outside AuthProvider');
  return ctx;
}
